/* Benutzereinstellungen im Backend (user_settings): Schnappschuss, Prüfung und Abgleich.
 * Reine Logik ohne DOM; Lesen/Anwenden, Zeitgeber und Client werden übergeben, damit sie sich mit node prüfen lässt.
 * Das Backend gewinnt beim Anmelden; Änderungen gehen entprellt zurück. localStorage bleibt Cache und Stand ohne Backend. */
/** Benutzereinstellungen, wie sie im Backend liegen (versioniertes JSON) */
export interface Snapshot {
  v?: number;
  lang?: string;
  theme?: { theme: string; custom: Record<string, Record<string, string>> };
  keys?: Record<string, string[]>;
}
/** Erlaubte Werte beim Prüfen (fehlt eine Liste, wird nur die Form geprüft) */
export interface Allow { langs?: string[]; themes?: string[]; keyIds?: string[] }
/** Was der Abgleich vom Client braucht */
export interface SyncClient {
  user: { id: string } | null;
  settingsGet(): Promise<{ id: string; data: unknown } | null>;
  settingsSave(id: string | null, userId: string, data: Snapshot): Promise<{ id?: string }>;
}
export interface SyncOptions {
  client: SyncClient;
  read: () => { lang?: string; theme?: Snapshot['theme']; keys?: Record<string, string[]> };
  apply: (part: Snapshot) => void;
  allow?: Allow;
  delay?: number;
  setTimer?: (f: () => void, ms: number) => unknown;
  clearTimer?: (t: unknown) => void;
  onStatus?: (kind: 'pulled' | 'seeded' | 'saved' | 'error', info?: unknown) => void;
}

const UserSettings = (function () {
  'use strict';
  const VERSION = 1;
  const MAX_BYTES = 60 * 1024;        // das Backend begrenzt auf 64 KB
  const HEX = /^#[0-9a-fA-F]{6}$/;

  const isObj = (x: unknown): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x);
  const sortedKeys = (o: object): string[] => Object.keys(o).sort();

  /** Stabile Textform (Schlüssel sortiert), damit gleiche Einstellungen gleich verglichen werden */
  function stable(v: unknown): string {
    if (Array.isArray(v)) return '[' + v.map(stable).join(',') + ']';
    if (isObj(v)) return '{' + sortedKeys(v).map((k) => JSON.stringify(k) + ':' + stable(v[k])).join(',') + '}';
    return JSON.stringify(v);
  }
  const equal = (a: unknown, b: unknown): boolean => stable(a) === stable(b);

  /** Schnappschuss aus { lang, theme:{theme,custom}, keys:{id:[…]} }; nur bekannte Teile, tief kopiert */
  function collect(src: { lang?: string; theme?: Snapshot['theme']; keys?: Record<string, string[]> }): Snapshot {
    const out: Snapshot = { v: VERSION };
    if (src.lang) out.lang = String(src.lang);
    if (src.theme) out.theme = JSON.parse(JSON.stringify({ theme: src.theme.theme, custom: src.theme.custom || {} }));
    if (src.keys) out.keys = JSON.parse(JSON.stringify(src.keys));
    return out;
  }

  /** Prüft Daten aus dem Backend und liefert nur die gültigen Teile (oder null bei falschem Format/neuerer Version).
   * allow: { langs:[…], themes:[…], keyIds:[…] } (fehlt ein Eintrag, wird nur die Form geprüft) */
  function sanitize(raw: unknown, allowed?: Allow): Snapshot | null {
    const allow = allowed || {};
    // Eingabe aus dem Netz: bewusst locker typisiert, jeder Teil wird einzeln geprüft
    const data = raw as Record<string, any>;
    const isO = isObj as (x: unknown) => boolean;
    if (!isO(data) || (data.v !== undefined && !(Number.isInteger(data.v) && data.v >= 1 && data.v <= VERSION))) return null;
    const out: Snapshot = {};
    if (typeof data.lang === 'string' && (!allow.langs || allow.langs.indexOf(data.lang) >= 0)) out.lang = data.lang;
    if (isO(data.theme) && typeof data.theme.theme === 'string' && (!allow.themes || allow.themes.indexOf(data.theme.theme) >= 0)) {
      const custom: Record<string, Record<string, string>> = {};
      if (isO(data.theme.custom)) {
        Object.keys(data.theme.custom).forEach((tid) => {
          if (allow.themes && allow.themes.indexOf(tid) < 0) return;
          const c = data.theme.custom[tid]; if (!isO(c)) return;
          const cc: Record<string, string> = {};
          Object.keys(c).forEach((tok) => { if (typeof c[tok] === 'string' && HEX.test(c[tok]) && tok.length <= 20) cc[tok] = c[tok]; });
          if (Object.keys(cc).length) custom[tid] = cc;
        });
      }
      out.theme = { theme: data.theme.theme, custom: custom };
    }
    if (isO(data.keys)) {
      const keys: Record<string, string[]> = {};
      Object.keys(data.keys).forEach((id) => {
        const v = data.keys[id];
        if (allow.keyIds && allow.keyIds.indexOf(id) < 0) return;
        if (Array.isArray(v) && v.length <= 8 && v.every((c) => typeof c === 'string' && c.length <= 40)) keys[id] = v.slice();
      });
      out.keys = keys;
    }
    return out;
  }

  /** Abgleich mit dem Backend.
   * opts: client { user, settingsGet(), settingsSave(id, userId, data) }, read() → Schnappschuss der lokalen Einstellungen,
   *       apply(teil) → wendet geprüfte Teile an, allow (für sanitize), delay (ms, Standard 1500),
   *       setTimer/clearTimer (für Tests), onStatus('pulled'|'seeded'|'saved'|'error', info) */
  function createSync(opts: SyncOptions) {
    const delay = opts.delay === undefined ? 1500 : opts.delay;
    const setT = opts.setTimer || ((f: () => void, ms: number): unknown => setTimeout(f, ms)), clearT = opts.clearTimer || ((t: unknown) => clearTimeout(t as ReturnType<typeof setTimeout>));
    const say = opts.onStatus || function () {};
    let id: string | null = null, last: unknown = null, timer: unknown = null, applying = false, busy = false, again = false, active = true;

    async function push() {
      if (!active) return;
      if (busy) { again = true; return; }
      busy = true;
      try {
        do {
          again = false;
          const snap = collect(opts.read());
          if (last && equal(snap, last)) break;
          if (JSON.stringify(snap).length > MAX_BYTES) { say('error', new Error('Einstellungen zu groß')); break; }
          const user = opts.client.user;
          if (!user) break;
          const rec = await opts.client.settingsSave(id, user.id, snap);
          id = rec.id || id; last = snap; say('saved');
        } while (again && active);
      } catch (err) { say('error', err); } finally { busy = false; }
    }

    return {
      /** nach dem Anmelden: Einstellungen holen und anwenden; gibt es noch keine, werden die lokalen gespeichert */
      async pull() {
        active = true;
        let rec;
        try { rec = await opts.client.settingsGet(); } catch (err) { say('error', err); return false; }
        if (!rec) { await push(); say('seeded'); return true; }
        id = rec.id;
        const part = sanitize(rec.data, opts.allow);
        if (part) {
          applying = true;
          try { opts.apply(part); } finally { applying = false; }
        }
        last = isObj(rec.data) ? rec.data : null;
        say('pulled', part);
        // Teile, die das Backend nicht kennt (z. B. älterer Stand), werden mit den lokalen ergänzt
        if (!part || !equal(collect(opts.read()), last)) this.changed();
        return true;
      },
      /** bei jeder lokalen Änderung aufrufen */
      changed() {
        if (applying || !active) return;
        if (timer) clearT(timer);
        timer = setT(() => { timer = null; push(); }, delay);
      },
      async flush() { if (timer) { clearT(timer); timer = null; } await push(); },
      stop() { active = false; if (timer) { clearT(timer); timer = null; } id = null; last = null; }
    };
  }

  const api = { VERSION, collect, sanitize, equal, stable, createSync };
  return api;
})();
export default UserSettings;
